import { useEffect, useLayoutEffect, useRef } from "react";

import { useSelectedFormula } from "../hooks";
import { useParsedFormula } from "../../../utils/formula-ast";

import { ExpressionDisplay } from "./ExpressionDisplay";

export const FormulaTree = (p: {
  formula: string;
  row: number;
  depth?: number;
  /** X of the parent card's arrow, relative to the formula container. */
  arrowX?: number;
}) => {
  const [selected, setSelected] = useSelectedFormula();
  const { ready, ast, explicitParenKeys } = useParsedFormula(p.formula);
  const depth = p.depth ?? 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear selection when the formula or row changes
  useEffect(() => setSelected(null), [p.formula, p.row]);

  // Align the LAST token of the expanded formula under the parent card's arrow.
  // Runs synchronously between commit and paint — no visible jump, no re-render.
  // Skipped entirely if the formula is too long to fit on one line.
  useLayoutEffect(() => {
    if (depth === 0 || p.arrowX === undefined) return;
    const wrapper = wrapperRef.current;
    const row = rowRef.current;
    if (!wrapper || !row) return;
    const container = wrapper.closest<HTMLElement>("[data-formula-container]");
    if (!container) return;
    const lastToken = row.lastElementChild as HTMLElement | null;
    if (!lastToken) return;

    // Reset margin so measurements reflect the natural (unaligned) position.
    wrapper.style.marginLeft = "";

    // Wrap detection: with flex-wrap, the row's height equals the tallest
    // child's height when on a single line. If it grew past that, the
    // formula has wrapped — leave it left-aligned and never break further.
    const tokens = Array.from(row.children) as HTMLElement[];
    if (tokens.length === 0) return;
    const maxTokenHeight = Math.max(...tokens.map((t) => t.getBoundingClientRect().height));
    const isWrapped = (): boolean => row.getBoundingClientRect().height > maxTokenHeight + 2;
    if (isWrapped()) return;

    const containerRect = container.getBoundingClientRect();
    const lastRect = lastToken.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const naturalLastCenter = lastRect.left + lastRect.width / 2 - containerRect.left;
    const naturalWrapperRight = wrapperRect.right - containerRect.left;

    const idealMargin = p.arrowX - naturalLastCenter;
    const maxMargin = Math.max(0, containerRect.width - naturalWrapperRight);
    const margin = Math.max(0, Math.min(idealMargin, maxMargin));
    if (margin <= 0) return;

    wrapper.style.marginLeft = `${margin}px`;

    // If applying the margin caused wrapping (edge case from minor padding
    // rounding), revert to the natural position.
    if (isWrapped()) wrapper.style.marginLeft = "";
  });

  return (
    <div ref={wrapperRef} className={depth > 0 ? "flex gap-0 w-fit mt-[calc(19px+3px)]" : "space-y-0 pl-4 w-max"}>
      {depth > 0 && <div className="w-[2px] shrink-0 rounded-full bg-[var(--colour-border-dark)]" />}
      <div className={depth > 0 ? "pl-2 space-y-0" : ""}>
        <div ref={rowRef} className="flex items-center gap-2 flex-nowrap relative py-1">
          {!ready && <span className="text-foreground-muted text-sm">Loading...</span>}
          {ready && !ast && <span className="text-foreground-muted text-sm">Invalid formula</span>}
          {ready && ast && (
            <ExpressionDisplay
              expr={ast}
              row={p.row}
              selected={selected}
              onSelect={setSelected}
              explicitParenKeys={explicitParenKeys}
            />
          )}
        </div>
        {selected && (
          <FormulaTree formula={selected.formula} row={selected.row} depth={depth + 1} arrowX={selected.arrowX} />
        )}
      </div>
    </div>
  );
};
